import { useState } from "react"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import { Checkbox } from "@/components/chadcn/Checkbox"
import { Label } from "@/components/chadcn/Label"
import type { QuickCheckReason } from "@/model/QuickCheckReason"

const QUESTION = "Why did you take this measurement?"
const PICK_TEXT = "Pick all that apply"
const SUBMIT_BUTTON = "Continue"
const ERROR_MIN = "Please select at least one reason"

type ReasonOption = { label: string; value: QuickCheckReason }
const reasonOptions: ReasonOption[] = [
  { label: "Curious", value: "curious" },
  { label: "Medication Change", value: "medication-change" },
  { label: "Tired", value: "tired" },
  { label: "Dizzy", value: "dizzy" },
  { label: "Light Headed", value: "light-headed" },
  { label: "Nausea", value: "nausea" },
  { label: "Other", value: "other" },
]

type Props = {
  reasons: QuickCheckReason[]
  onSubmit: (reasons: QuickCheckReason[]) => void
  onBack?: () => void
}

const ReasonPickerStep = ({ reasons, onSubmit, onBack }: Props) => {
  const [selected, setSelected] = useState<QuickCheckReason[]>(reasons)
  const [error, setError] = useState<string | null>(null)

  const toggle = (value: QuickCheckReason, checked: boolean) => {
    setError(null)
    setSelected((current) =>
      checked ? [...current, value] : current.filter((r) => r !== value),
    )
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (selected.length === 0) {
      setError(ERROR_MIN)
      return
    }
    onSubmit(selected)
  }

  return (
    <form
      className="flex min-h-[calc(100vh-32px)] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      <BackButton customClickHandler={onBack} />
      <h2 className="text-h2 text-primary-contrast">{QUESTION}</h2>

      <div className="mt-6 flex flex-1 flex-col gap-2">
        <Label className="text-h4">{PICK_TEXT}</Label>
        <div className="flex flex-col gap-2">
          {reasonOptions.map(({ label, value }) => {
            const isChecked = selected.includes(value)
            return (
              <label
                key={value}
                className="flex cursor-pointer items-center gap-3 px-2 py-2"
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={(checked) => toggle(value, checked === true)}
                />
                <span className="text-h4 font-medium text-primary-contrast">{label}</span>
              </label>
            )
          })}
        </div>
        {error && <p className="text-sm text-error">{error}</p>}
      </div>

      <Button type="submit" className="w-full py-2 text-base">
        {SUBMIT_BUTTON}
      </Button>
    </form>
  )
}

export default ReasonPickerStep
